import React, { useState, useEffect, useCallback } from 'react';
import { businessUnitsApi, businessLinesApi, teamsApi, usersApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Plus, Trash, PencilSimple, Buildings, Warning, Users } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const OrganizationPage = () => {
  const { isAdmin, isManager } = useAuth();
  const [businessUnits, setBusinessUnits] = useState([]);
  const [businessLines, setBusinessLines] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [isUnitDialogOpen, setIsUnitDialogOpen] = useState(false);
  const [isLineDialogOpen, setIsLineDialogOpen] = useState(false);
  const [isTeamDialogOpen, setIsTeamDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', business_unit_id: '', business_line_id: '', team_leader_id: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [unitsRes, linesRes, teamsRes] = await Promise.all([
        businessUnitsApi.getAll(),
        businessLinesApi.getAll(),
        teamsApi.getAll(),
      ]);
      setBusinessUnits(unitsRes.data);
      setBusinessLines(linesRes.data);
      setTeams(teamsRes.data);

      // Fetch team leaders
      const usersRes = await usersApi.getAll({ role: 'team_leader' });
      setTeamLeaders(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch organization data:', error);
      toast.error('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({ name: '', description: '', business_unit_id: '', business_line_id: '', team_leader_id: 'none' });
    setEditingItem(null);
    setError('');
  };

  // Business Unit handlers
  const openUnitDialog = (unit = null) => {
    if (unit) {
      setEditingItem(unit);
      setFormData({ name: unit.name, description: unit.description || '' });
    } else {
      resetForm();
    }
    setIsUnitDialogOpen(true);
  };

  const handleUnitSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (editingItem) {
        await businessUnitsApi.update(editingItem.id, { name: formData.name, description: formData.description });
        toast.success('Business unit updated');
      } else {
        await businessUnitsApi.create({ name: formData.name, description: formData.description });
        toast.success('Business unit created');
      }
      setIsUnitDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteUnit = async (id) => {
    if (!window.confirm('Are you sure? This will also delete all business lines and teams under this unit.')) return;
    try {
      await businessUnitsApi.delete(id);
      toast.success('Business unit deleted');
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Business Line handlers
  const openLineDialog = (line = null, unitId = null) => {
    if (line) {
      setEditingItem(line);
      setFormData({ name: line.name, description: line.description || '', business_unit_id: line.business_unit_id });
    } else {
      resetForm();
      setFormData((prev) => ({ ...prev, business_unit_id: unitId || '' }));
    }
    setIsLineDialogOpen(true);
  };

  const handleLineSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (editingItem) {
        await businessLinesApi.update(editingItem.id, {
          name: formData.name,
          description: formData.description,
          business_unit_id: formData.business_unit_id,
        });
        toast.success('Business line updated');
      } else {
        await businessLinesApi.create({
          name: formData.name,
          description: formData.description,
          business_unit_id: formData.business_unit_id,
        });
        toast.success('Business line created');
      }
      setIsLineDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteLine = async (id) => {
    if (!window.confirm('Are you sure? This will also delete all teams under this business line.')) return;
    try {
      await businessLinesApi.delete(id);
      toast.success('Business line deleted');
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Team handlers
  const openTeamDialog = (team = null, lineId = null) => {
    if (team) {
      setEditingItem(team);
      setFormData({
        name: team.name,
        description: team.description || '',
        business_line_id: team.business_line_id,
        team_leader_id: team.team_leader_id || 'none',
      });
    } else {
      resetForm();
      setFormData((prev) => ({ ...prev, business_line_id: lineId || '' }));
    }
    setIsTeamDialogOpen(true);
  };

  const handleTeamSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (editingItem) {
        await teamsApi.update(editingItem.id, {
          name: formData.name,
          description: formData.description,
          business_line_id: formData.business_line_id,
          team_leader_id: formData.team_leader_id && formData.team_leader_id !== 'none' ? formData.team_leader_id : null,
        });
        toast.success('Team aggiornato');
      } else {
        await teamsApi.create({
          name: formData.name,
          description: formData.description,
          business_line_id: formData.business_line_id,
          team_leader_id: formData.team_leader_id && formData.team_leader_id !== 'none' ? formData.team_leader_id : null,
        });
        toast.success('Team creato');
      }
      setIsTeamDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteTeam = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    try {
      await teamsApi.delete(id);
      toast.success('Team deleted');
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const getLinesForUnit = (unitId) => businessLines.filter((l) => l.business_unit_id === unitId);
  const getTeamsForLine = (lineId) => teams.filter((t) => t.business_line_id === lineId);
  const getTeamLeaderName = (id) => teamLeaders.find((u) => u.id === id)?.name || '-';

  return (
    <div data-testid="organization-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">Organizzazione</h1>
          <p className="text-[#52525B] mt-1">Gestisci unità di business, linee e team</p>
        </div>

        {isAdmin && (
          <Button
            onClick={() => openUnitDialog()}
            className="bg-[#002FA7] text-white px-6 py-2.5 font-medium hover:bg-[#00278A] rounded-none"
            data-testid="add-business-unit-btn"
          >
            <Plus size={20} weight="bold" className="mr-2" />
            Aggiungi Unità di Business
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : businessUnits.length === 0 ? (
        <div className="border border-[#E4E4E7] p-12 text-center">
          <Buildings size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <h3 className="font-heading font-bold text-lg mb-2">Nessuna unità di business</h3>
          <p className="text-[#52525B] mb-4">Crea la prima unità di business per iniziare.</p>
          {isAdmin && (
            <Button
              onClick={() => openUnitDialog()}
              className="bg-[#002FA7] text-white px-6 py-2.5 font-medium hover:bg-[#00278A] rounded-none"
            >
              <Plus size={20} weight="bold" className="mr-2" />
              Aggiungi Unità di Business
            </Button>
          )}
        </div>
      ) : (
        <Accordion type="multiple" className="space-y-4">
          {businessUnits.map((unit) => (
            <AccordionItem
              key={unit.id}
              value={unit.id}
              className="border border-[#E4E4E7] bg-white"
              data-testid={`business-unit-${unit.id}`}
            >
              <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-[#FAFAFA]">
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-10 h-10 bg-[#0A0A0A] flex items-center justify-center">
                    <Buildings className="text-white" size={20} weight="bold" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-heading font-bold text-lg">{unit.name}</h3>
                    <p className="text-xs text-[#52525B]">{getLinesForUnit(unit.id).length} linee di business</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <div className="flex items-center justify-between mb-4 pt-4 border-t border-[#E4E4E7]">
                  <p className="text-sm text-[#52525B]">{unit.description || 'Nessuna descrizione'}</p>
                  {isAdmin && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => openUnitDialog(unit)}
                        className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#0A0A0A] transition-colors"
                        data-testid={`edit-unit-${unit.id}`}
                      >
                        <PencilSimple size={16} weight="bold" />
                      </button>
                      <button
                        onClick={() => deleteUnit(unit.id)}
                        className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#FF2A00] hover:text-[#FF2A00] transition-colors"
                        data-testid={`delete-unit-${unit.id}`}
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                      <Button
                        size="sm"
                        onClick={() => openLineDialog(null, unit.id)}
                        className="bg-[#002FA7] text-white px-4 py-1.5 text-sm hover:bg-[#00278A] rounded-none"
                        data-testid={`add-line-to-${unit.id}`}
                      >
                        <Plus size={16} weight="bold" className="mr-1" />
                        Aggiungi Linea
                      </Button>
                    </div>
                  )}
                </div>

                {/* Business Lines */}
                <div className="space-y-3 ml-4 border-l-2 border-[#E4E4E7] pl-4">
                  {getLinesForUnit(unit.id).map((line) => (
                    <div key={line.id} className="border border-[#E4E4E7] p-4" data-testid={`business-line-${line.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold">{line.name}</h4>
                          <p className="text-xs text-[#52525B]">{line.description || 'Nessuna descrizione'}</p>
                        </div>
                        {(isAdmin || isManager) && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => openLineDialog(line)}
                              className="w-7 h-7 flex items-center justify-center border border-[#E4E4E7] hover:border-[#0A0A0A] transition-colors"
                              data-testid={`edit-line-${line.id}`}
                            >
                              <PencilSimple size={14} weight="bold" />
                            </button>
                            <button
                              onClick={() => deleteLine(line.id)}
                              className="w-7 h-7 flex items-center justify-center border border-[#E4E4E7] hover:border-[#FF2A00] hover:text-[#FF2A00] transition-colors"
                              data-testid={`delete-line-${line.id}`}
                            >
                              <Trash size={14} weight="bold" />
                            </button>
                            <Button
                              size="sm"
                              onClick={() => openTeamDialog(null, line.id)}
                              className="bg-[#0A0A0A] text-white px-3 py-1 text-xs hover:bg-[#52525B] rounded-none"
                              data-testid={`add-team-to-${line.id}`}
                            >
                              <Plus size={14} weight="bold" className="mr-1" />
                              Team
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Teams */}
                      <div className="space-y-2 ml-4 border-l-2 border-[#E4E4E7] pl-4">
                        {getTeamsForLine(line.id).map((team) => (
                          <div
                            key={team.id}
                            className="flex items-center justify-between py-2 px-3 bg-[#FAFAFA] border border-[#E4E4E7]"
                            data-testid={`team-${team.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <Users size={16} className="text-[#52525B]" />
                              <div>
                                <span className="font-medium text-sm">{team.name}</span>
                                <span className="text-xs text-[#52525B] ml-2">
                                  Leader: {getTeamLeaderName(team.team_leader_id)}
                                </span>
                              </div>
                            </div>
                            {(isAdmin || isManager) && (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => openTeamDialog(team)}
                                  className="w-6 h-6 flex items-center justify-center hover:bg-white transition-colors"
                                  data-testid={`edit-team-${team.id}`}
                                >
                                  <PencilSimple size={12} weight="bold" />
                                </button>
                                <button
                                  onClick={() => deleteTeam(team.id)}
                                  className="w-6 h-6 flex items-center justify-center hover:text-[#FF2A00] transition-colors"
                                  data-testid={`delete-team-${team.id}`}
                                >
                                  <Trash size={12} weight="bold" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                        {getTeamsForLine(line.id).length === 0 && (
                          <p className="text-xs text-[#52525B] py-2">Nessun team in questa linea di business</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {getLinesForUnit(unit.id).length === 0 && (
                    <p className="text-sm text-[#52525B] py-2">No business lines in this unit</p>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Business Unit Dialog */}
      <Dialog open={isUnitDialogOpen} onOpenChange={setIsUnitDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-none border border-[#0A0A0A]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {editingItem ? 'Modifica Unità di Business' : 'Aggiungi Unità di Business'}
            </DialogTitle>
          </DialogHeader>
          {error && (
            <div className="bg-[#FF2A00]/5 border border-[#FF2A00] p-3 flex items-start gap-2">
              <Warning className="text-[#FF2A00] flex-shrink-0 mt-0.5" size={16} weight="bold" />
              <p className="text-[#FF2A00] text-sm">{error}</p>
            </div>
          )}
          <form onSubmit={handleUnitSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Nome</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-none border-[#E4E4E7]"
                data-testid="unit-name-input"
                required
              />
            </div>
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Descrizione</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-none border-[#E4E4E7] min-h-20"
                data-testid="unit-description-input"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsUnitDialogOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">
                Annulla
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-[#002FA7] text-white hover:bg-[#00278A] rounded-none" data-testid="submit-unit-btn">
                {submitting ? 'Salvataggio...' : editingItem ? 'Aggiorna' : 'Crea'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Business Line Dialog */}
      <Dialog open={isLineDialogOpen} onOpenChange={setIsLineDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-none border border-[#0A0A0A]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {editingItem ? 'Modifica Linea di Business' : 'Aggiungi Linea di Business'}
            </DialogTitle>
          </DialogHeader>
          {error && (
            <div className="bg-[#FF2A00]/5 border border-[#FF2A00] p-3 flex items-start gap-2">
              <Warning className="text-[#FF2A00] flex-shrink-0 mt-0.5" size={16} weight="bold" />
              <p className="text-[#FF2A00] text-sm">{error}</p>
            </div>
          )}
          <form onSubmit={handleLineSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Business Unit</Label>
              <Select value={formData.business_unit_id} onValueChange={(v) => setFormData({ ...formData, business_unit_id: v })}>
                <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="line-unit-select">
                  <SelectValue placeholder="Seleziona unità" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {businessUnits.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id} className="rounded-none">{unit.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Nome</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-none border-[#E4E4E7]"
                data-testid="line-name-input"
                required
              />
            </div>
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Descrizione</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-none border-[#E4E4E7] min-h-20"
                data-testid="line-description-input"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsLineDialogOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">
                Annulla
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-[#002FA7] text-white hover:bg-[#00278A] rounded-none" data-testid="submit-line-btn">
                {submitting ? 'Salvataggio...' : editingItem ? 'Aggiorna' : 'Crea'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Team Dialog */}
      <Dialog open={isTeamDialogOpen} onOpenChange={setIsTeamDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-none border border-[#0A0A0A]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {editingItem ? 'Modifica Team' : 'Aggiungi Team'}
            </DialogTitle>
          </DialogHeader>
          {error && (
            <div className="bg-[#FF2A00]/5 border border-[#FF2A00] p-3 flex items-start gap-2">
              <Warning className="text-[#FF2A00] flex-shrink-0 mt-0.5" size={16} weight="bold" />
              <p className="text-[#FF2A00] text-sm">{error}</p>
            </div>
          )}
          <form onSubmit={handleTeamSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Business Line</Label>
              <Select value={formData.business_line_id} onValueChange={(v) => setFormData({ ...formData, business_line_id: v })}>
                <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="team-line-select">
                  <SelectValue placeholder="Seleziona linea di business" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {businessLines.map((line) => (
                    <SelectItem key={line.id} value={line.id} className="rounded-none">{line.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Nome</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-none border-[#E4E4E7]"
                data-testid="team-name-input"
                required
              />
            </div>
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Team Leader</Label>
              <Select value={formData.team_leader_id} onValueChange={(v) => setFormData({ ...formData, team_leader_id: v })}>
                <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="team-leader-select">
                  <SelectValue placeholder="Seleziona team leader (facoltativo)" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="none" className="rounded-none">Nessuno</SelectItem>
                  {teamLeaders.map((leader) => (
                    <SelectItem key={leader.id} value={leader.id} className="rounded-none">{leader.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Descrizione</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-none border-[#E4E4E7] min-h-20"
                data-testid="team-description-input"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsTeamDialogOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">
                Annulla
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-[#002FA7] text-white hover:bg-[#00278A] rounded-none" data-testid="submit-team-btn">
                {submitting ? 'Salvataggio...' : editingItem ? 'Aggiorna' : 'Crea'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationPage;
